import { type Placement } from '@atlaskit/dropdown-menu/types';
import { MenuGroup, Section } from '@atlaskit/menu';
import { type TriggerProps } from '@atlaskit/popup';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import Popup, { type PopupProps } from '@databases/atlaskit/Popup';
import { useCommentStateContext } from '@databases/comments/hooks/useCommentContext';
import { DATABASE_CELL_CONTEXT_MENU_OPEN_ATTR } from '@databases/frontend-metrics/metrics/cell-context-menu';
import React, {
  type FunctionComponent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';

import { isDrilldownMenu, DrilldownHeader, DrilldownWrapper } from './DrilldownMenu';
import MenuContentShell from './MenuContentShell';
import MenuItemRow from './MenuItemRow';
import ScrollGradientWrapper from './ScrollGradientWrapper';
import { type MenuData } from './contextual-menu-state';
import { useContextualMenu } from './hooks/useContextualMenu';
import { useDrilldownStack } from './hooks/useDrilldownStack';
import { MenuNavContext, type MenuPage } from './hooks/useMenuNav';
import { SharedSubmenuStateContext, useSubmenuState } from './hooks/useSubmenuState';
import type { MenuItems } from './types';

export type { MenuItem } from './types';

const CONTEXTUAL_MENU_MIN_WIDTH = 248;

const menuContainerStyles = xcss({
  display: 'flex',
  flexDirection: 'column',
  overflowY: 'auto',
  maxHeight: `calc(100vh - ${token('space.200', '16px')})`,
});

const stickyHeaderStyles = xcss({
  position: 'sticky',
  top: 'space.0',
  backgroundColor: 'elevation.surface.overlay',
  zIndex: 'navigation',
});

const stickyFooterStyles = xcss({
  position: 'sticky',
  bottom: 'space.0',
  backgroundColor: 'elevation.surface.overlay',
  zIndex: 'navigation',
});

type NestedPopupProps = {
  footer?: React.ReactNode;
  items: MenuItems;
  onClose: (event?: Event | React.MouseEvent | React.KeyboardEvent) => void;
  initialPage?: MenuPage;
  /**
   * Optional data-* attributes spread onto the root `MenuGroup`. Used to stamp
   * cell-context-menu open markers consumed by the
   * `cell-context-menu-open-interaction-time` performance metric.
   */
  menuGroupDataAttributes?: Record<string, string>;
};

const NestedPopup: FunctionComponent<NestedPopupProps> = ({
  items,
  onClose,
  initialPage,
  footer,
  menuGroupDataAttributes,
}) => {
  const submenuState = useSubmenuState();
  const { activeSubmenuKey, closeAll: closeAllSubmenus } = submenuState;

  const { nav, currentPage, depth, isContentPage, handleKeyDown } = useDrilldownStack({
    onClose,
    closeAllSubmenus,
    activeSubmenuKey,
    initialPage,
  });

  // When a drilldown page provides structured items, show those instead of root items.
  const currentItems = useMemo(
    () => (isContentPage ? [] : currentPage?.items ?? items),
    [isContentPage, currentPage, items]
  );

  const { openThread } = useCommentStateContext();
  useEffect(() => {
    if (openThread.valueId) {
      onClose?.();
    }
  }, [onClose, openThread.valueId]);

  const nonEmptyGroups = useMemo(
    () => currentItems.filter((group) => group.items.length > 0),
    [currentItems]
  );

  const shouldShowSeparator = useCallback(
    (index: number) => {
      const menuGroup = nonEmptyGroups[index];
      return index > 0 && !menuGroup.hasNoSeparator;
    },
    [nonEmptyGroups]
  );

  const effectiveMinWidth = currentPage?.width ?? CONTEXTUAL_MENU_MIN_WIDTH;
  const hasDrilldownItems = items.some((group) =>
    group.items.some((item) => isDrilldownMenu(item.content))
  );
  const hasDrilldownActive = hasDrilldownItems || depth > 0;

  return (
    <MenuNavContext.Provider value={nav}>
      <SharedSubmenuStateContext.Provider value={submenuState}>
        <DrilldownWrapper active={hasDrilldownActive} onKeyDown={handleKeyDown}>
          <MenuGroup
            minWidth={effectiveMinWidth}
            maxWidth={currentPage?.width}
            onClick={(e) => e.stopPropagation()}
            spacing="compact"
            data-contextual-menu="true"
            data-testid="database-context-menu"
            {...menuGroupDataAttributes}
          >
            <Box xcss={menuContainerStyles} testId="contextual-menu-scroll-container">
              <ScrollGradientWrapper>
                {currentPage && currentPage.title && (
                  <Box xcss={stickyHeaderStyles} testId="contextual-menu-sticky-header">
                    <DrilldownHeader
                      title={currentPage.title}
                      onMouseEnter={closeAllSubmenus}
                      headerAction={currentPage.headerAction}
                    />
                  </Box>
                )}

                {isContentPage && currentPage && currentPage.content && (
                  <MenuContentShell>{currentPage.content(nav)}</MenuContentShell>
                )}

                {nonEmptyGroups.map((group, index) => (
                  <Section
                    hasSeparator={shouldShowSeparator(index)}
                    title={group.sectionTitle}
                    key={index}
                  >
                    {group.items?.map((item) => (
                      <MenuItemRow
                        key={item.key}
                        item={item}
                        nav={nav}
                        submenuState={submenuState}
                        onClose={onClose}
                      />
                    ))}
                  </Section>
                ))}

                {footer && (
                  <Box xcss={stickyFooterStyles} testId="contextual-menu-sticky-footer">
                    {footer}
                  </Box>
                )}
              </ScrollGradientWrapper>
            </Box>
          </MenuGroup>
        </DrilldownWrapper>
      </SharedSubmenuStateContext.Provider>
    </MenuNavContext.Provider>
  );
};

export type ContextualMenuProps = Pick<
  PopupProps,
  'shouldRenderToParent' | 'shouldDisableFocusLock' | 'autoFocus'
> & {
  menuData: MenuData;
  items?: MenuItems;
  trigger: (props: TriggerProps) => React.ReactNode;
  onClose?: (event?: Event | React.MouseEvent | React.KeyboardEvent | null) => void;
  offset?: [number, number];
  boundariesElement?: HTMLElement;
  placement?: Placement;
  shouldDisableFocusLock?: boolean;
  panel?: boolean;
  isActionBarEnabled?: boolean;
  rootPage?: MenuPage;
  footer?: React.ReactNode;
};

export const ContextualMenu: FunctionComponent<ContextualMenuProps> = ({
  menuData,
  trigger: Trigger,
  items = [{ items: [] }],
  onClose,
  offset,
  boundariesElement = document.body,
  placement = 'right-start',
  shouldRenderToParent,
  shouldDisableFocusLock,
  autoFocus,
  panel = false,
  isActionBarEnabled = false,
  rootPage,
  footer,
}) => {
  const { closeMenu, isMenuOpen, isPanel } = useContextualMenu(menuData);
  const handleCloseMenu = useCallback(
    (e?: Event | React.MouseEvent | React.KeyboardEvent | null) => {
      closeMenu();
      onClose?.(e);
    },
    [closeMenu, onClose]
  );
  const isPopupOpen = useMemo(
    () =>
      isMenuOpen &&
      (rootPage != null ||
        (items.length > 0 && items.some((item) => item.items.length > 0))),
    [isMenuOpen, items, rootPage]
  );

  const renderPopup = !isActionBarEnabled ? isPopupOpen && isPanel === panel : false;

  const popupContainerRef = useRef<HTMLDivElement | null>(null);

  // @atlaskit/popup uses focus-trap which pulls focus back when it escapes its container.
  // PopupSelect components (e.g. SearchableFieldPicker) render their dropdowns in portals
  // outside this container. This listener intercepts those focus events before the trap
  // can steal focus back.
  useLayoutEffect(() => {
    if (!renderPopup) {
      return;
    }

    const handler = (e: FocusEvent) => {
      const target = e.target;
      if (!(target instanceof HTMLElement)) {
        return;
      }
      // No need to intercept if the focus target is already inside the popup container
      if (popupContainerRef.current?.contains(target)) {
        return;
      }

      let node: HTMLElement | null = target;
      while (node && node !== document.body) {
        if (node.parentElement?.querySelector(':scope > [data-focus-guard]')) {
          e.stopImmediatePropagation();
          return;
        }
        node = node.parentElement;
      }
    };

    document.addEventListener('focus', handler, true);
    return () => {
      document.removeEventListener('focus', handler, true);
    };
  }, [renderPopup]);

  const menuGroupDataAttributes = useMemo(
    () =>
      menuData.type === 'value'
        ? {
            [DATABASE_CELL_CONTEXT_MENU_OPEN_ATTR]: '',
            'data-entry-id': menuData.entryId,
            'data-field-id': menuData.fieldId,
          }
        : undefined,
    [menuData]
  );

  return (
    <Popup
      role="menu"
      disableContainerScroll
      shouldDisableFocusLock={shouldDisableFocusLock}
      shouldRenderToParent={shouldRenderToParent}
      {...(autoFocus !== undefined ? { autoFocus } : {})}
      shouldFitViewport
      isOpen={renderPopup}
      onClose={handleCloseMenu}
      content={() => (
        <div ref={popupContainerRef}>
          <NestedPopup
            items={items}
            onClose={handleCloseMenu}
            initialPage={rootPage}
            footer={footer}
            menuGroupDataAttributes={menuGroupDataAttributes}
          />
        </div>
      )}
      placement={placement}
      trigger={Trigger}
      offset={offset}
      boundary={boundariesElement}
      contentContainer={null}
      id="database-contextual-menu"
      data-testid="database-contextual-menu"
    />
  );
};

export default ContextualMenu;
